<template>
  <div ref="stickerGallery" class="sticker-gallery">
    <div class="sticker-gallery__chip-tag-container">
      <file-selector :accept="logoAccept" @file-selected="uploadLogo">
        <chip-tag 
          text="Upload logo"
          has-fill
          has-no-border
        >
          <cloud-upload-icon />
        </chip-tag>
      </file-selector>
      <chip-tag
        v-for="collection in stickerGalleryStore.collections"
        :text="collection.title"
        :key="collection.id"
        :is-active="selectedCollection?.title === collection.title"
        @click="onSelectTag(collection.title)"
      />
    </div>
    <mosaic-grid
      class="sticker-gallery__results"
      v-show="hasResults"
      :items="stickerGalleryStore.items"
      :num-cols="2"
      :gap="4"
    >
      <template #default="{ item, index }">
        <div class="sticker-gallery__results-item" @click="addStickerToCanvas(item)">
          <lazy-image
            class="sticker-gallery__results-item-contents"
            :src="item.preview.imageSource.src"
            :parent-container="stickerGallery"
            @viewport-intersect="onIntersect(index)"
            @dragstart="onDrag($event, item)"
          />
        </div>
      </template>
    </mosaic-grid>
    <div class="sticker-gallery__no-results-text" v-show="!hasResults">
      No search results
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import { useStickerGalleryStore } from '@/store/gallery/sticker-store'
import LazyImage from '@/components/gallery/LazyImage.vue'
import MosaicGrid from '@/components/gallery/MosaicGrid.vue'
import { GalleryItem } from '@/store/gallery/types'
import ChipTag from '@/components/gallery/ChipTag.vue'
import FileSelector from '@/components/common/FileSelector.vue'
import { logoAccept } from '@/static/file-types'
import { useApplicationStore } from '@/store/application-store'
import CloudUploadIcon from '@/assets/CloudUploadIcon.svg'
import { storeToRefs } from 'pinia'
import GtmHelper from '@/analytics/gtm-helper'
import { useFileValidator, ValidationError } from '@/composables/UseFileValidator'
import useImageStates from '@/composables/UseImageStates'

const gtmHelper = GtmHelper.getInstance()
const applicationStore = useApplicationStore()
const stickerGalleryStore = useStickerGalleryStore()
const { selectedCollection } = storeToRefs(stickerGalleryStore)

const stickerGallery = ref<HTMLElement | null>(null)

const hasResults = computed(() => stickerGalleryStore.items.length > 0)

const { validateStickerFile } = useFileValidator()
const { addLogoSticker, addSticker } = useImageStates()

onBeforeMount(() => {
  // Fetch the default collection media
  stickerGalleryStore.fetchCollections().then(() => retrieveActiveCollection('Emoji'))
})

const onDrag = (event: DragEvent, sticker: GalleryItem) => {
  event.dataTransfer?.setData('src', sticker.source[0].src)
}

const uploadLogo = (file: File) => {
  try {
    validateStickerFile(file)
    addLogoSticker(URL.createObjectURL(file), file.name)
    gtmHelper.triggerLogoUploadGtmEvent(file.size)
  } catch (e) {
    if (e instanceof ValidationError) {
      applicationStore.pushSnackbarMessage({
        header: e.title,
        text: e.message,
        state: 'error'
      })
    }
  }
}

// Threshold for when to start fetching the next page, might change later
const threshold = 0.9

const onIntersect = (index: number) => {
  if (index === Math.floor((stickerGalleryStore.items.length - 1) * threshold)) {
    stickerGalleryStore.nextPage()
  }
}

const addStickerToCanvas = (sticker: GalleryItem) => {
  if (applicationStore.isStickerLoading) {
    return
  }
  addSticker(sticker.source[0].src)
}

const onSelectTag = (title: string) => {
  if (title !== selectedCollection.value?.title) {
    retrieveActiveCollection(title)
  }
}

const retrieveActiveCollection = (title: string) => {
  const collection = stickerGalleryStore.collections.find((collection) => collection.title === title)
  if (collection) {
    // Asynchronous call
    stickerGalleryStore.fetchByCollection(collection)
  }
}

</script>
<style lang="scss">
.sticker-gallery {
  display: flex;
  flex-direction: column;
  padding: 0px 32px 32px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }

  &__chip-tag-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 32px;
    margin-bottom: 12px;
  }

  &__no-results-text {
    color: $white;
    font-size: 14px;
    text-align: center;
    margin-top: 56px;
  }

  &__results {
    margin-top: 20px;

    &-item, &-item-contents {
      height: 100%;
      width: 100%;
      border-radius: 4px;
    }

    &-item {
      position: relative;
      background: $grey-90; // Placeholder background
      
      &::after {
        content: '';
        transition: border 150ms $animationCurve;
        border: 1px solid transparent;
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: inherit;
        pointer-events: none;
      }

      .lazy-image {
        vertical-align: text-bottom;

        &--loaded {
          // Checkerboard background with 40% opacity
          $checkerboard-size: 42.5px;
          background: repeating-conic-gradient(rgba($black, 40%) 0% 25%, rgba($grey-80, 40%) 0% 50%) 0/#{$checkerboard-size $checkerboard-size};
        }
      }

      // White hover border
      &:hover {
        &::after {
          border: 1px solid $white;
        }
      }
      // mousedown
      &:active::after {
        border: 3px solid $white;
      }
    }
  }
}
</style>
